import { Search as SearchIcon } from '@mui/icons-material';
import MuiAutocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import is from '@sindresorhus/is';
import Highlighter from 'react-highlight-words';

import type { TextFieldProps } from '@mui/material/TextField';
import type { ChangeEvent, FocusEventHandler, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

export type WithId = { readonly id: number | string };

type AutoCompleteOnChange<T> = (option: T | null) => void;

// eslint-disable-next-line functional/prefer-readonly-type
type FilterOptions<T> = (options: T[]) => T[];

export type AutocompleteProps<T extends WithId> = {
    readonly id: string;
    readonly filterOptions?: FilterOptions<T>;
    readonly getOptionLabel: (option: T) => string;
    readonly helperText?: string | undefined;
    readonly inputRef?: Ref<HTMLElement>;
    readonly inputValue?: string;
    readonly isBoxed?: boolean;
    readonly isDisabled?: boolean;
    readonly isError?: boolean;
    readonly isFocused?: boolean;
    readonly label: string;
    readonly onBlur?: FocusEventHandler<HTMLElement>;
    readonly onChange: AutoCompleteOnChange<T>;
    readonly onInputChange?: (event: ChangeEvent, value: string) => void;
    readonly options: ReadonlyArray<T>;
    readonly value?: T | null;
};

export const checkForEqualId = <T extends WithId>(first: T, second: T): boolean => first.id === second.id;

export const Autocomplete = <T extends WithId>({
    id,
    filterOptions,
    getOptionLabel,
    helperText,
    inputRef,
    inputValue = '',
    isBoxed,
    isDisabled,
    isError,
    label,
    onBlur,
    onChange,
    onInputChange,
    options,
    value,
}: AutocompleteProps<T>): ReactNode => {
    const optionalInputRef: TextFieldProps = is.undefined(inputRef) ? {} : { inputRef };

    const inputProps: TextFieldProps =
        isBoxed === true
            ? {
                  placeholder: label,
                  size: 'small',
              }
            : {
                  label,
                  variant: 'standard',
              };

    const defaultFilterOptions: FilterOptions<T> = (options) =>
        options.filter((option) => {
            const label = getOptionLabel(option);

            return label.toLowerCase().includes(inputValue.toLowerCase());
        });

    return (
        <FormControl error={isError ?? false} fullWidth={true} variant="standard">
            <MuiAutocomplete
                disabled={isDisabled ?? false}
                filterOptions={filterOptions ?? defaultFilterOptions}
                getOptionLabel={getOptionLabel}
                id={id}
                inputValue={inputValue}
                isOptionEqualToValue={checkForEqualId}
                options={options}
                popupIcon={<SearchIcon />}
                renderInput={(params) => (
                    <TextField
                        error={isError ?? false}
                        {...(params as TextFieldProps)}
                        {...inputProps}
                        {...optionalInputRef}
                    />
                )}
                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: T): ReactElement<any> => (
                    <li {...props} key={option.id}>
                        <Highlighter
                            autoEscape={true}
                            highlightTag="strong"
                            searchWords={[inputValue]}
                            textToHighlight={getOptionLabel(option)}
                        />
                    </li>
                )}
                sx={{
                    '& .MuiAutocomplete-popupIndicator': { transform: 'none' },
                }}
                value={value ?? null}
                onBlur={onBlur}
                onChange={(_, option) => onChange(option)}
                onInputChange={onInputChange}
            />
            {is.nonEmptyString(helperText) && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};
